import React, { useState, useEffect } from 'react';
import { Plus, Minus, Package, DollarSign, Hash, Filter } from 'lucide-react';
import { rentalApi } from '../services/api';
import { RentalItem, ReservationRentalItemCreate } from '../types';
import { showInfo } from '../utils/toast';
import { formatCurrency, multiplyCurrency } from '../utils/currency'; // Issue #905: Decimal.js utilities

interface RentalItemSelectorProps {
  fairId: number;
  reservationId?: number; // Optional, for existing reservations
  selectedItems: ReservationRentalItemCreate[];
  onItemsChange: (items: ReservationRentalItemCreate[]) => void;
  readonly?: boolean;
}

const CATEGORY_OPTIONS = [
  { value: '', label: 'All Categories' },
  { value: 'furniture', label: 'Furniture', emoji: '🪑' },
  { value: 'lighting', label: 'Lighting', emoji: '💡' },
  { value: 'audio', label: 'Audio', emoji: '🔊' },
  { value: 'display', label: 'Display', emoji: '🖼️' },
  { value: 'power', label: 'Power', emoji: '🔌' },
  { value: 'tent', label: 'Tent/Canopy', emoji: '⛺' },
  { value: 'other', label: 'Other', emoji: '📦' },
];

export default function RentalItemSelector({
  fairId,
  selectedItems,
  onItemsChange,
  readonly = false,
}: RentalItemSelectorProps) {
  const [items, setItems] = useState<RentalItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedCategory, setSelectedCategory] = useState('');

  useEffect(() => {
    const loadItems = async () => {
      try {
        setLoading(true);
        const params: { category?: string } = {};
        if (selectedCategory) {
          params.category = selectedCategory;
        }
        const response = await rentalApi.browseRentalItems(fairId, params);
        setItems(response.items.filter((item) => item.is_active));
        setError(null);
      } catch (err: unknown) {
        console.error('Failed to load rental items:', err);
        setError('Failed to load rental items');
      } finally {
        setLoading(false);
      }
    };

    loadItems();
  }, [fairId, selectedCategory]);

  const getItemQuantity = (itemId: number): number => {
    const selected = selectedItems.find((i) => i.rental_item_id === itemId);
    return selected?.quantity || 0;
  };

  const handleQuantityChange = (item: RentalItem, newQuantity: number) => {
    if (readonly) return;

    const available = item.quantity_available - item.quantity_reserved;

    // Validate quantity
    if (newQuantity < 0) return;
    if (newQuantity > available) {
      showInfo(`Only ${available} units available`);
      return;
    }

    const updatedItems = selectedItems.filter((i) => i.rental_item_id !== item.id);

    if (newQuantity > 0) {
      updatedItems.push({
        rental_item_id: item.id,
        quantity: newQuantity,
      });
    }

    onItemsChange(updatedItems);
  };

  const getCategoryEmoji = (category?: string) => {
    const cat = CATEGORY_OPTIONS.find((c) => c.value === category);
    return cat?.emoji || '📦';
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
        {error}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Filter */}
      {!readonly && (
        <div className="flex items-center gap-2">
          <Filter className="w-5 h-5 text-gray-400" />
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
          >
            {CATEGORY_OPTIONS.map((cat) => (
              <option key={cat.value} value={cat.value}>
                {cat.emoji ? `${cat.emoji} ` : ''}{cat.label}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Items List */}
      {items.length === 0 ? (
        <div className="bg-gray-50 rounded-lg p-8 text-center">
          <Package className="w-12 h-12 text-gray-400 mx-auto mb-2" />
          <p className="text-gray-600">No rental equipment available for this fair.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((item) => {
            const quantity = getItemQuantity(item.id);
            const available = item.quantity_available - item.quantity_reserved;
            const isSelected = quantity > 0;

            return (
              <div
                key={item.id}
                className={`border rounded-lg p-4 transition-all ${
                  isSelected ? 'border-primary bg-teal-50' : 'border-gray-200 bg-white'
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-2xl">{getCategoryEmoji(item.category)}</span>
                      <div>
                        <h4 className="font-medium text-gray-900">{item.name}</h4>
                        {item.description && (
                          <p className="text-sm text-gray-600">{item.description}</p>
                        )}
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-4 text-sm">
                      {/* Category */}
                      <div className="flex items-center gap-1">
                        <span className="px-2 py-1 text-xs rounded-full bg-moonbone text-ink">
                          {item.category || 'other'}
                        </span>
                      </div>

                      {/* Pricing */}
                      <div className="flex items-center gap-1 text-gray-700">
                        <DollarSign className="w-4 h-4" />
                        {formatCurrency(item.price_per_day).replace('$', '')}/day {/* Issue #905: Decimal.js formatting */}
                        {item.price_per_event && (
                          <span className="ml-2 text-gray-500">
                            or {formatCurrency(item.price_per_event)}/event {/* Issue #905: Decimal.js formatting */}
                          </span>
                        )}
                      </div>

                      {/* Availability */}
                      <div className="flex items-center gap-1 text-gray-700">
                        <Hash className="w-4 h-4" />
                        <span className={available === 0 ? 'text-red-600 font-medium' : ''}>
                          {available} available
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Quantity Selector */}
                  {!readonly && (
                    <div className="ml-4">
                      {available === 0 ? (
                        <span className="px-3 py-2 bg-red-100 text-red-800 rounded-lg text-sm font-medium">
                          Sold Out
                        </span>
                      ) : (
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleQuantityChange(item, quantity - 1)}
                            disabled={quantity === 0}
                            aria-label={`Decrease quantity of ${item.name}`}
                            className="p-1 rounded-lg border border-gray-300 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <Minus className="w-4 h-4" />
                          </button>
                          <span className="w-12 text-center font-medium">{quantity}</span>
                          <button
                            onClick={() => handleQuantityChange(item, quantity + 1)}
                            disabled={quantity >= available}
                            aria-label={`Increase quantity of ${item.name}`}
                            className="p-1 rounded-lg border border-gray-300 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <Plus className="w-4 h-4" />
                          </button>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Read-only quantity display */}
                  {readonly && quantity > 0 && (
                    <div className="ml-4">
                      <span className="px-3 py-2 bg-teal-100 text-primary-dark rounded-lg text-sm font-medium">
                        Qty: {quantity}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Summary */}
      {selectedItems.length > 0 && (
        <div className="mt-4 p-4 bg-teal-50 border border-teal-200 rounded-lg">
          <h4 className="font-medium text-teal-900 mb-2">Selected Rental Items</h4>
          <div className="space-y-1 text-sm">
            {selectedItems.map((selected) => {
              const item = items.find((i) => i.id === selected.rental_item_id);
              if (!item) return null;

              return (
                <div key={selected.rental_item_id} className="flex justify-between text-primary-dark">
                  <span>
                    {item.name} × {selected.quantity}
                  </span>
                  <span className="font-medium">
                    {/* Issue #905: Decimal.js for multiplication */}
                    {formatCurrency(multiplyCurrency(item.price_per_day, selected.quantity))}/day
                  </span>
                </div>
              );
            })}
          </div>
          <div className="mt-2 pt-2 border-t border-teal-300 flex justify-between font-medium text-teal-900">
            <span>Total Items:</span>
            <span>{selectedItems.reduce((sum, item) => sum + item.quantity, 0)} units</span>
          </div>
        </div>
      )}
    </div>
  );
}
